import { BILLING_CYCLE, SUBSCRIPTION_TYPE, WEBINAR_ONE_TIME_CAPACITY } from '@/common/constants';
import _ from 'lodash';
import { webinarSingleUseAllowed } from '@/common/service/op-switch';

export default {
  computed: {
    allAvailableCapacityOrderList() {
      return this.allCapacityOrderList.filter(capacity => {
        const isSelected = this.selectedPlanList.find(p => p.capacity === capacity);
        return !isSelected;
      });
    },
    classifyAvailablePlansByCycle() {
      return this.planList.sort((a, b) => a.capacity - b.capacity).reduce((prev, cur) => {
        const isSelected = this.selectedPlanList.find(p => p.capacity == cur.capacity);
        if (isSelected) return prev;
        if (cur.billing_cycle === BILLING_CYCLE.ONE_TIME) {
          prev.availableOneTimeOptions.push(cur);
        } else {
          prev.availableSubAndOneMonthOptions.push(cur);
        }
        return prev;
      }, {
        availableSubAndOneMonthOptions: [],
        availableOneTimeOptions: [],
      });
    },
  },
  methods: {
    mx_getCurrentCapacityGroupOptions(options) {
      const subAndOneMonthOptions = [], oneTimeOptions = [];
      options.forEach(capacity => {
        if (webinarSingleUseAllowed && WEBINAR_ONE_TIME_CAPACITY.includes(capacity)) {
          oneTimeOptions.push(capacity);
        } else {
          subAndOneMonthOptions.push(capacity);
        }
      });
      const hasOneMonthPlan = this.planList.find(p => p.billing_cycle === BILLING_CYCLE.ONE_MONTH);
      const subAndOneMonthOptionsLabel = hasOneMonthPlan ? this.$t('billing.billing_cycle.subscription_or_one_month') : this.$t('billing.billing_cycle.subscription');
      return [{
        label: subAndOneMonthOptionsLabel,
        options: subAndOneMonthOptions,
        type: SUBSCRIPTION_TYPE.recurring_and_one_month,
      }, {
        label: this.$t('billing.billing_cycle.one_time'),
        options: oneTimeOptions,
        type: SUBSCRIPTION_TYPE.one_time,
      }];
    },
    mx_getBillingCycleGroupOptions(_, capacity) {
      const { availableSubAndOneMonthOptions, availableOneTimeOptions } = this.classifyAvailablePlansByCycle;
      let subAndOneMonthOptions = [],
        oneTimeOptions = [];
      // plan dropdown should display all available options
      if (webinarSingleUseAllowed && WEBINAR_ONE_TIME_CAPACITY.includes(capacity)) {
        subAndOneMonthOptions = availableSubAndOneMonthOptions.length ? this.mx_getBillingCycleList(availableSubAndOneMonthOptions[0].capacity) : [];
        oneTimeOptions = this.mx_getBillingCycleList(capacity);
      } else {
        subAndOneMonthOptions = this.mx_getBillingCycleList(capacity);
        oneTimeOptions = availableOneTimeOptions.length ? this.mx_getBillingCycleList(availableOneTimeOptions[0].capacity) : [];
      }
      const { capacityDescList, oneTimeCapacityDescList } = this.getCapacityByDesc();
      return [{
        label: capacityDescList.length && this.$t('billing.single_use_meeting.a_to_b_attendees', [capacityDescList[0], capacityDescList[capacityDescList.length - 1]]),
        options: subAndOneMonthOptions,
        type: SUBSCRIPTION_TYPE.recurring_and_one_month,
      }, {
        label: oneTimeCapacityDescList.length && this.$t('billing.single_use_meeting.a_to_b_attendees', [oneTimeCapacityDescList[0], oneTimeCapacityDescList[oneTimeCapacityDescList.length - 1]]),
        options: oneTimeOptions,
        type: SUBSCRIPTION_TYPE.one_time,
      }];
    },
    getLatestAddCapacityPlan() {
      const configRows = Object.keys(this.$refs).filter(key => /configRow\d+/.test(key)).map(key => {
        const instance = this.$refs[key]?.[0];
        if (instance) {
          const attrs = instance.$attrs;
          return {
            index: attrs.index,
            capacity: attrs.capacity,
            billing_cycle: instance.billingCycle,
          };
        }
        return null;
      }).filter(k => k).sort((a, b) => b.index - a.index);
      let { capacity, billing_cycle } = configRows?.[0] || this.selectedPlanList[this.selectedPlanList.length - 1];
      return {
        capacity,
        billing_cycle,
      };
    },
    getCapacityByDesc() {
      return [...new Set(this.planList.map(p => ({
        capacity: p.capacity,
        capacityDesc: p.capacityDesc,
      })))]
        .sort((a, b) => a.capacity - b.capacity)
        .reduce((prev, cur) => {
          const { capacityDescList, oneTimeCapacityDescList } = prev;
          if (WEBINAR_ONE_TIME_CAPACITY.includes(cur.capacity)) {
            oneTimeCapacityDescList.push(cur.capacityDesc);
          } else {
            capacityDescList.push(cur.capacityDesc);
          }
          return prev;
        }, {
          capacityDescList: [],
          oneTimeCapacityDescList: [],
        });
    },
    getCapacityListByType(list) {
      return list.reduce((prev, cur) => {
        if (WEBINAR_ONE_TIME_CAPACITY.includes(cur)) {
          prev.oneTimeCapacityList.push(cur);
        } else {
          prev.capacityList.push(cur);
        }
        return prev;
      }, {
        capacityList: [],
        oneTimeCapacityList: [],
      });
    },
    handleAddCapacity(event) {
      let { capacity, billing_cycle } = this.getLatestAddCapacityPlan();
      const {
        capacityList: availableCapacityList,
        oneTimeCapacityList: availableOneTimeCapacityList,
      } = this.getCapacityListByType(this.allAvailableCapacityOrderList);
      const { capacityList, oneTimeCapacityList } = this.getCapacityListByType(this.allCapacityOrderList);
      let nextCapacity;
      if (capacityList.includes(capacity)) {
        nextCapacity = availableCapacityList[0];
      } else if (oneTimeCapacityList.includes(capacity)) {
        nextCapacity = availableOneTimeCapacityList[0];
      }
      // all single-use webinar or all subscriptions and one-month webinar has been selected
      if (!nextCapacity) {
        if (!availableCapacityList.length) {
          nextCapacity = availableOneTimeCapacityList[0];
          billing_cycle = BILLING_CYCLE.ONE_TIME;
        }

        if (!availableOneTimeCapacityList.length) {
          nextCapacity = availableCapacityList[0];
          billing_cycle = BILLING_CYCLE.ONE_MONTH;
        }
      }
      const nextPlan = this.planList.find(p => p.capacity === nextCapacity && p.billing_cycle === billing_cycle);
      if (nextPlan?.plan_code) {
        this.updateSelectPlan({
          plan_code: nextPlan.plan_code,
          cartInfo: {
            selected_number: 1,
          },
        });
      };
      this.visibleCapacityList = _.concat(this.visibleCapacityList, nextCapacity);
      this.handleAddCapacityTracking(event, nextCapacity);
    },

  }
}
